import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Button, Divider, Stack, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import ModalComponent from '@components/modal/Modal.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {Colors} from '@constants/colors.constants';
import {useObservable} from '@ngneat/react-rxjs';
import {LoadingButton} from '@mui/lab';
import {
  InvoiceCreation,
  InvoiceCreationBase,
  invoicesQuery,
  invoicesService,
  InvoiceTypology
} from '@store/invoices/invoices';
import AsyncMultiSelectWithSearchComponent
  from '@components/inputs/asyncMultiSelect/AsyncMultiSelectWithSearch.component';
import {map, of} from 'rxjs';
import dayjs from '@utils/dayjs.utils';
import {AdminRoutes, InvoicesRoutes} from '@utils/routes.utils';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {TableFilterType} from '@store/common/tableFilter.model';
import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import SelectComponent from '@components/inputs/select/Select.component';
import {FacturationRuleType} from '@store/clients/details/client-details.enums';

interface GenerateBillModalProps {
  handleClose?: () => void;
  forNestle?: boolean;
}

const GenerateBillModal = (props: GenerateBillModalProps) => {
  const {handleClose, forNestle = false} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const [bill, setBill] = useState<Partial<InvoiceCreation>>({
    documentDate: new Date(),
    reminderDate: new Date(),
    invoicingRule: FacturationRuleType.CLIENT_UNIT_DISTINCT
  });

  const [{loading}] = useObservable(invoicesQuery.postBill$);

  const updateBill = (name: string) => (value: any) => setBill((state) => ({...state, [name]: value}));

  const handleSave = () => {
    const updatedBillDto: InvoiceCreationBase = {
      from: bill.from!,
      to: bill.to!,
      clientIds: bill.clientItems?.map((it) => it.value) ?? [],
      clientUnitIds: bill.clientUnitItems?.map((it) => it.value) ?? [],
      typologies: bill.typologyItems?.map((it) => it.value) ?? [],
      documentDate: bill.documentDate!,
      reminderDate: bill.reminderDate!,
      invoicingRule: bill.invoicingRule!,
    };
    invoicesService.postInvoice(updatedBillDto, forNestle).subscribe({
      next: (res: { jobInvoiceId: number; error: any }) => {
        if (res.error) {
          enqueueSnackbar(t('invoices.bills.create.error'), {variant: 'error'});
          return;
        }
        enqueueSnackbar(t('invoices.bills.create.success'), {variant: 'success'});
        navigate(`/${AdminRoutes.INVOICES}/${InvoicesRoutes.INVOICES_VALIDATION}/${res.jobInvoiceId}`);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const errors = {
    from: getFieldError(bill.from, FieldValidationType.REQUIRED_DATE),
    to: getFieldError(bill.to, FieldValidationType.REQUIRED_DATE),
    documentDate: getFieldError(bill.documentDate, FieldValidationType.REQUIRED_DATE),
    reminderDate: getFieldError(bill.reminderDate, FieldValidationType.REQUIRED_DATE),
    invoicingRule: getFieldError(bill.invoicingRule, FieldValidationType.REQUIRED_STRING),
  };

  return (
    <ModalComponent
      customColor={Colors.white}
      content={(
        <Stack width="95%" spacing={2} pt="55px" pb="10px">
          <Typography fontWeight="bold">
            {t('invoices.bills.create.title')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <DateRangePickerComponent
              startDate={bill.from}
              endDate={bill.to}
              handleChangeStartDate={(from) => updateBill('from')(from ? dayjs(from).startOf('day').toDate() : undefined)}
              handleChangeEndDate={(to) => updateBill('to')(to ? dayjs(to).endOf('day').toDate() : undefined)}
              error={errors.from || errors.to}/>
          </Stack>
          {!forNestle &&
              <>
                  <Divider/>
                  <Typography fontWeight="bold">
                    {t('invoices.bills.create.clients')}
                  </Typography>
                  <AsyncMultiSelectWithSearchComponent
                      placeholder={t('invoices.bills.create.clientsPlaceholder')}
                      handleChange={updateBill('clientItems')}
                      values={bill.clientItems ?? []}
                      getOptions={(search) => filtersService
                        .getFilters(TableFilterType.CLIENT_ORGANISATION, FilterBaseEntityEnum.NONE, [], search)
                        .pipe(
                          map((options) =>
                            options.map((o) => ({label: o.name || '-', value: o.id}))
                          )
                        )}/>
                  <AsyncMultiSelectWithSearchComponent
                      placeholder={t('invoices.bills.create.clientUnitsPlaceholder')}
                      handleChange={updateBill('clientUnitItems')}
                      values={bill.clientUnitItems ?? []}
                      hint={t('invoices.bills.create.clientUnitsHint')}
                      getOptions={(search) => filtersService
                        .getFilters(TableFilterType.CLIENT_ORGANISATION_UNIT, FilterBaseEntityEnum.NONE, [{
                          type: TableFilterType.CLIENT_ORGANISATION,
                          values: bill.clientItems || []
                        }], search)
                        .pipe(
                          map((options) =>
                            options.map((o) => ({label: o.name || '-', value: o.id}))
                          )
                        )}/>
                  <Typography fontWeight="bold" alignSelf="center">
                    {t('invoices.bills.create.or')}
                  </Typography>
                  <Typography fontWeight="bold">
                    {t('invoices.bills.create.typologies')}
                  </Typography>
                  <AsyncMultiSelectWithSearchComponent
                      placeholder={t('invoices.bills.create.typologiesPlaceholder')}
                      handleChange={updateBill('typologyItems')}
                      values={bill.typologyItems ?? []}
                      getOptions={() => of(InvoiceTypology.selectItems())}/>
              </>
          }
          <Divider/>
          <Stack direction="row" spacing={2}>
            { !forNestle && <Stack spacing={0.5} flex={1}>
              <Typography fontSize="16px" fontWeight="400">
                {t('invoices.bills.create.invoicingRule')}
              </Typography>
              <SelectComponent value={bill.invoicingRule} handleChange={(value) => updateBill('invoicingRule')(value)}
                               items={FacturationRuleType.selectItems().filter((s) => [FacturationRuleType.CLIENT_UNIT_DISTINCT, FacturationRuleType.INVOICE_ADDRESS].includes(s.value))}/>
            </Stack> }
            <Stack spacing={0.5} flex={1}>
              <Typography fontSize="16px" fontWeight="400">
                {t('invoices.bills.create.reminderDate')}
              </Typography>
              <DatePickerComponent
                value={bill.reminderDate}
                handleChange={(reminderDate) => updateBill('reminderDate')(reminderDate)}
                error={errors.reminderDate}/>
            </Stack>
          </Stack>
        </Stack>
      )}
      actions={(
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            color="primary">
            {t('global.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            disabled={checkIfErrors(errors)}
            loading={loading}
            onClick={handleSave}
            color="primary">
            {t('global.save')}
          </LoadingButton>
        </>
      )}/>
  );
};

export default GenerateBillModal;
